/**
 * Campaign-tagged onboarding-link telemetry: fire-and-forget, write-only,
 * strictly allowlisted (landing path, the 5 UTM tags, the resolved
 * axis/filter, and a taskId only once it's confirmed to resolve to a real
 * task) — never the full URL, prompt/task text, or email. Recorded once per
 * campaign/filter combination per browser session (sessionStorage), so
 * revisiting the same short link in the same tab never double-counts.
 */
import { request, ReqMethods } from "./apiClient";

const { POST } = ReqMethods;

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];
const SESSION_PREFIX = "campaignTelemetrySent:";

/** Pure: builds the allowlisted payload, or null when the arrival isn't campaign-tagged. */
export function buildCampaignPayload(landingPath, search, axis, label, taskId) {
  const params = new URLSearchParams(search || "");
  const utm = {};
  UTM_KEYS.forEach((key) => {
    const value = params.get(key);
    if (value) utm[key] = value;
  });
  if (Object.keys(utm).length === 0) return null;
  return {
    landingPath,
    ...utm,
    filters: { [axis]: label },
    ...(taskId ? { taskId } : {}),
  };
}

/** Pure: a stable per-session dedupe key for one campaign/filter combination. */
export function campaignDedupeKey(payload) {
  const filterPart = Object.entries(payload.filters || {})
    .map(([k, v]) => `${k}=${v}`)
    .join(",");
  return `${payload.utm_campaign || ""}|${filterPart}`.toLowerCase();
}

export function recordCampaignArrival({ landingPath, search, axis, label, taskId }) {
  const payload = buildCampaignPayload(landingPath, search, axis, label, taskId);
  if (!payload) return;

  const sessionKey = `${SESSION_PREFIX}${campaignDedupeKey(payload)}`;
  try {
    if (sessionStorage.getItem(sessionKey)) return;
    sessionStorage.setItem(sessionKey, "1");
  } catch {
    // sessionStorage unavailable — still attempt the write, just without dedupe
  }

  request(POST, "/onboarding-telemetry", { payload }).catch(() => {
    // best-effort only — telemetry must never block or break the redirect
  });
}
